import { useCallback, useEffect, useRef, useState } from 'react';
import type { DragEvent, KeyboardEvent, PointerEvent, RefObject } from 'react';

/**
 * Generic drag and drop between the columns of a board, with no dependency.
 *
 * Three ways in, one way out (`onMove`):
 *  - mouse: native HTML5 drag and drop;
 *  - touch: a long press picks the card up (native drag does not fire on phones), then a finger drag and release.
 *    A normal swipe still scrolls the board because nothing is picked up until the press has been held;
 *  - keyboard: Space/Enter picks up, Left/Right chooses the column, Space/Enter drops, Escape cancels.
 *
 * The hook owns no data. The board decides what a move does (optimistic update, API call, rollback).
 */

export interface BoardColumn { id: string; label: string }
export interface BoardItemRef { id: string; columnId: string; label: string }
export type BoardDragMode = 'mouse' | 'touch' | 'keyboard';

export interface BoardMove {
  itemId: string;
  label: string;
  from: string;
  to: string;
  via: BoardDragMode;
}

export interface BoardDragState {
  itemId: string;
  label: string;
  fromColumn: string;
  /** The column the item would land in if dropped now; null when over no column. */
  overColumn: string | null;
  mode: BoardDragMode;
}

export interface UseBoardDragOptions {
  columns: readonly BoardColumn[];
  /** Called once per real move (never for a drop on the same column). Owns its own error handling. */
  onMove: (move: BoardMove) => void | Promise<void>;
  /** Viewers: nothing is draggable and no handler does anything. */
  disabled?: boolean;
  /** Items with a save in flight; they cannot be picked up again. */
  lockedItemIds?: ReadonlySet<string>;
  /** Columns an item may not be dropped into. Defaults to every column. */
  canDrop?: (itemId: string, from: string, to: string) => boolean;
  /** The sideways scroller: auto-scrolled near its edges while dragging, and searched to restore focus. */
  containerRef?: RefObject<HTMLElement | null>;
  /** How long a touch must be held before the card is picked up. */
  longPressMs?: number;
}

export const LONG_PRESS_MS = 350;
/** A finger that moves further than this before the long press fires is scrolling, not dragging. */
export const TOUCH_SLOP_PX = 8;
export const EDGE_ZONE_PX = 56;
export const EDGE_MAX_SPEED_PX = 18;
const COLUMN_ATTR = 'data-board-column';
const ITEM_ATTR = 'data-board-item';

/** Pixels to scroll this frame (negative = left) for a pointer at clientX; 0 away from the edges. */
export function edgeScrollDelta(
  clientX: number,
  rect: { left: number; right: number },
  zone = EDGE_ZONE_PX,
  maxSpeed = EDGE_MAX_SPEED_PX,
): number {
  if (clientX < rect.left + zone) return -Math.round(maxSpeed * Math.min(1, (rect.left + zone - clientX) / zone));
  if (clientX > rect.right - zone) return Math.round(maxSpeed * Math.min(1, (clientX - (rect.right - zone)) / zone));
  return 0;
}

export function useBoardDrag(options: UseBoardDragOptions) {
  const { columns, onMove, disabled = false, lockedItemIds, canDrop, containerRef, longPressMs = LONG_PRESS_MS } = options;

  const [drag, setDragState] = useState<BoardDragState | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const [focusTick, setFocusTick] = useState(0);
  const dragRef = useRef<BoardDragState | null>(null);
  const pendingFocus = useRef<string | null>(null);
  const lastPointerType = useRef<string>('mouse');
  const lastX = useRef<number | null>(null);
  const touchCleanup = useRef<(() => void) | null>(null);

  // Handlers are recreated every render, but the window listeners of a touch drag outlive a render: read the latest.
  const latest = useRef({ columns, onMove, canDrop, lockedItemIds, disabled });
  latest.current = { columns, onMove, canDrop, lockedItemIds, disabled };

  const setDrag = useCallback((next: BoardDragState | null) => {
    dragRef.current = next;
    setDragState(next);
  }, []);

  const announce = useCallback((message: string) => setAnnouncement(message), []);

  const columnLabel = (id: string) => latest.current.columns.find(c => c.id === id)?.label ?? id;
  const columnPosition = (id: string) => `Column ${columnLabel(id)}, ${latest.current.columns.findIndex(c => c.id === id) + 1} of ${latest.current.columns.length}.`;
  const allowed = (itemId: string, from: string, to: string) => latest.current.canDrop?.(itemId, from, to) ?? true;

  const finish = useCallback((message?: string) => {
    touchCleanup.current?.();
    touchCleanup.current = null;
    lastX.current = null;
    setDrag(null);
    if (message) setAnnouncement(message);
  }, [setDrag]);

  /** Completes a drop. The one place a move is reported, whatever the input method. */
  const drop = useCallback((state: BoardDragState, to: string | null) => {
    const { fromColumn: from, itemId, label, mode } = state;
    if (to === null || to === from) {
      finish(to === from && mode === 'keyboard' ? `${label} stays in ${columnLabel(from)}.` : undefined);
      return;
    }
    if (!allowed(itemId, from, to)) {
      finish(`${label} cannot be moved to ${columnLabel(to)}.`);
      return;
    }
    if (mode === 'keyboard') pendingFocus.current = itemId;
    finish(`Moved ${label} to ${columnLabel(to)}.`);
    Promise.resolve(latest.current.onMove({ itemId, label, from, to, via: mode })).catch(() => {
      // onMove reports its own failures (toast and rollback); this only keeps a rejection from going unhandled.
    });
  }, [finish]);

  const cancel = useCallback((silent = false) => {
    const state = dragRef.current;
    if (!state) return;
    finish(silent ? undefined : 'Move cancelled.');
  }, [finish]);

  const isBlocked = (itemId: string) => latest.current.disabled || Boolean(latest.current.lockedItemIds?.has(itemId));

  // Cancel a drag that the board stops allowing (role changed, board unmounted).
  useEffect(() => {
    if (disabled && dragRef.current) finish();
  }, [disabled, finish]);
  useEffect(() => () => { touchCleanup.current?.(); }, []);

  // After a keyboard drop the card re-mounts in its new column; give it focus back.
  useEffect(() => {
    const id = pendingFocus.current;
    if (!id) return;
    pendingFocus.current = null;
    const root: ParentNode = containerRef?.current ?? document;
    const el = Array.from(root.querySelectorAll<HTMLElement>(`[${ITEM_ATTR}]`)).find(n => n.getAttribute(ITEM_ATTR) === id);
    el?.focus();
  }, [drag, focusTick, containerRef]);

  /** For a board that rolls a move back: put keyboard focus on the card again once it is re-rendered. */
  const restoreFocus = useCallback((itemId: string) => {
    pendingFocus.current = itemId;
    setFocusTick(t => t + 1);
  }, []);

  // Auto-scroll the board sideways while a card is held near its left or right edge.
  const active = drag !== null && drag.mode !== 'keyboard';
  useEffect(() => {
    if (!active || typeof window.requestAnimationFrame !== 'function') return undefined;
    let frame = 0;
    const tick = () => {
      const el = containerRef?.current;
      if (el && lastX.current !== null) {
        const delta = edgeScrollDelta(lastX.current, el.getBoundingClientRect());
        if (delta !== 0) el.scrollLeft += delta;
      }
      frame = window.requestAnimationFrame(tick);
    };
    frame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frame);
  }, [active, containerRef]);

  const setOver = (overColumn: string | null) => {
    const cur = dragRef.current;
    if (!cur || cur.overColumn === overColumn) return;
    setDrag({ ...cur, overColumn });
  };

  // ---- touch ---------------------------------------------------------------------------------------------
  const startTouch = (e: PointerEvent<HTMLElement>, item: BoardItemRef) => {
    const startX = e.clientX;
    const startY = e.clientY;
    let pressed = false;
    const timer = window.setTimeout(() => {
      pressed = true;
      lastX.current = startX;
      setDrag({ itemId: item.id, label: item.label, fromColumn: item.columnId, overColumn: item.columnId, mode: 'touch' });
      setAnnouncement(`Picked up ${item.label}. Drag to a column and release.`);
      if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') navigator.vibrate(12);
    }, longPressMs);

    const columnAt = (x: number, y: number): string | null => {
      const hit = typeof document.elementFromPoint === 'function' ? document.elementFromPoint(x, y) : null;
      return hit?.closest(`[${COLUMN_ATTR}]`)?.getAttribute(COLUMN_ATTR) ?? null;
    };
    const onMovePointer = (ev: globalThis.PointerEvent) => {
      if (!pressed) {
        if (Math.hypot(ev.clientX - startX, ev.clientY - startY) > TOUCH_SLOP_PX) stop(); // the user is scrolling
        return;
      }
      lastX.current = ev.clientX;
      setOver(columnAt(ev.clientX, ev.clientY));
    };
    const onUp = (ev: globalThis.PointerEvent) => {
      const state = dragRef.current;
      const was = pressed;
      stop();
      if (!was || !state) return;
      drop(state, columnAt(ev.clientX, ev.clientY));
    };
    const onCancel = () => {
      const was = pressed;
      stop();
      if (was) cancel();
    };
    // Once held, stop the page and board scrolling under the finger. Registered up front and non-passive so it can.
    const onTouchMove = (ev: TouchEvent) => { if (pressed && ev.cancelable) ev.preventDefault(); };
    const onContextMenu = (ev: Event) => { if (pressed) ev.preventDefault(); };

    function stop() {
      window.clearTimeout(timer);
      window.removeEventListener('pointermove', onMovePointer);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('contextmenu', onContextMenu);
      touchCleanup.current = null;
    }
    window.addEventListener('pointermove', onMovePointer);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('contextmenu', onContextMenu);
    touchCleanup.current = stop;
  };

  // ---- props ---------------------------------------------------------------------------------------------
  const getItemProps = (item: BoardItemRef) => {
    const blocked = isBlocked(item.id);
    return {
      [ITEM_ATTR]: item.id,
      draggable: !disabled && !blocked,
      onPointerDown: (e: PointerEvent<HTMLElement>) => {
        lastPointerType.current = e.pointerType;
        if (disabled || blocked || e.pointerType !== 'touch' || dragRef.current) return;
        if ((e.target as HTMLElement).closest('button, a, input, select, textarea')) return;
        touchCleanup.current?.();
        startTouch(e, item);
      },
      onDragStart: (e: DragEvent<HTMLElement>) => {
        // A long press on a phone can also start a native drag; the touch path owns that gesture.
        if (disabled || blocked || lastPointerType.current === 'touch' || dragRef.current) { e.preventDefault(); return; }
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', item.id); // Firefox will not start a drag without data
        const state: BoardDragState = { itemId: item.id, label: item.label, fromColumn: item.columnId, overColumn: item.columnId, mode: 'mouse' };
        dragRef.current = state;
        // Render the dimmed card after the browser has taken its drag image, or the image is the dimmed card.
        window.setTimeout(() => { if (dragRef.current === state) setDragState(state); }, 0);
      },
      onDragEnd: () => {
        // Still set means it was not dropped on a column (released elsewhere or Escape).
        if (dragRef.current?.mode === 'mouse') finish();
      },
      onKeyDown: (e: KeyboardEvent<HTMLElement>) => {
        if (e.target !== e.currentTarget || disabled) return; // the arrow buttons inside keep their own keys
        const cur = dragRef.current;
        const mine = cur !== null && cur.mode === 'keyboard' && cur.itemId === item.id;
        const confirmKey = e.key === ' ' || e.key === 'Enter';
        if (!mine) {
          if (!confirmKey || cur) return;
          e.preventDefault();
          if (blocked) { setAnnouncement(`${item.label} is still saving.`); return; }
          setDrag({ itemId: item.id, label: item.label, fromColumn: item.columnId, overColumn: item.columnId, mode: 'keyboard' });
          setAnnouncement(`Picked up ${item.label}. ${columnPosition(item.columnId)} Use the left and right arrow keys to choose a column, Space to drop, Escape to cancel.`);
          return;
        }
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
          e.preventDefault();
          const cols = latest.current.columns;
          const step = e.key === 'ArrowLeft' ? -1 : 1;
          let i = cols.findIndex(c => c.id === cur.overColumn);
          do { i += step; } while (i >= 0 && i < cols.length && !allowed(item.id, cur.fromColumn, cols[i].id) && cols[i].id !== cur.fromColumn);
          if (i < 0 || i >= cols.length) { setAnnouncement(`${columnPosition(cur.overColumn ?? cur.fromColumn)} No further columns that way.`); return; }
          setDrag({ ...cur, overColumn: cols[i].id });
          setAnnouncement(columnPosition(cols[i].id));
        } else if (confirmKey) {
          e.preventDefault();
          drop(cur, cur.overColumn);
        } else if (e.key === 'Escape') {
          e.preventDefault();
          e.stopPropagation();
          finish(`Move cancelled. ${item.label} stays in ${columnLabel(cur.fromColumn)}.`);
        }
      },
      onBlur: () => {
        // Tabbing away from a picked-up card drops the pick-up rather than leaving the board in a hidden mode.
        const cur = dragRef.current;
        if (cur?.mode === 'keyboard' && cur.itemId === item.id) finish(`Move cancelled. ${item.label} stays in ${columnLabel(cur.fromColumn)}.`);
      },
    };
  };

  const getColumnProps = (columnId: string) => ({
    [COLUMN_ATTR]: columnId,
    onDragEnter: (e: DragEvent<HTMLElement>) => {
      if (dragRef.current?.mode !== 'mouse') return;
      e.preventDefault();
    },
    onDragOver: (e: DragEvent<HTMLElement>) => {
      const cur = dragRef.current;
      if (!cur || cur.mode !== 'mouse') return;
      lastX.current = e.clientX;
      if (!allowed(cur.itemId, cur.fromColumn, columnId)) { e.dataTransfer.dropEffect = 'none'; setOver(null); return; }
      e.preventDefault(); // marks the column as a valid drop target
      e.dataTransfer.dropEffect = 'move';
      setOver(columnId);
    },
    onDragLeave: (e: DragEvent<HTMLElement>) => {
      const cur = dragRef.current;
      if (!cur || cur.mode !== 'mouse') return;
      if (e.relatedTarget instanceof Node && e.currentTarget.contains(e.relatedTarget)) return;
      if (cur.overColumn === columnId) setOver(null);
    },
    onDrop: (e: DragEvent<HTMLElement>) => {
      const cur = dragRef.current;
      if (!cur || cur.mode !== 'mouse') return;
      e.preventDefault();
      drop(cur, columnId);
    },
  });

  return {
    drag,
    announcement,
    announce,
    restoreFocus,
    cancel,
    isDragging: (itemId: string) => drag?.itemId === itemId,
    isOver: (columnId: string) => drag !== null && drag.overColumn === columnId && drag.overColumn !== drag.fromColumn,
    /** Props for the draggable card: spread them on the element. */
    getItemProps,
    /** Props for the column drop target: spread them on the element that wraps the column. */
    getColumnProps,
  };
}
